import React from 'react';
import { cn } from './cn';

const PADDING = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-5',
};

/**
 * Card — elevated surface container.
 *   padding: none | sm | md (default) | lg
 *   elevation: e0 (flat, border only) | e1 (default)
 *   interactive: adds hover state for clickable cards
 */
export function Card({
  padding = 'md',
  elevation = 'e1',
  interactive = false,
  as: Comp = 'div',
  className,
  children,
  ...rest
}) {
  return (
    <Comp
      className={cn(
        'bg-surface rounded-lg border border-line',
        elevation === 'e1' && 'shadow-e1',
        interactive && 'transition-colors duration-150 hover:bg-ink-50 dark:hover:bg-ink-800 cursor-pointer',
        PADDING[padding],
        className,
      )}
      {...rest}
    >
      {children}
    </Comp>
  );
}

export function CardHeader({ title, description, actions, className }) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4 border-b border-line px-5 py-4',
        className,
      )}
    >
      <div className="min-w-0">
        {title && <h3 className="text-h2 text-fg truncate">{title}</h3>}
        {description && <p className="mt-0.5 text-caption text-fg-muted">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export function CardBody({ className, children }) {
  return <div className={cn('p-5', className)}>{children}</div>;
}

export function CardFooter({ className, children }) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-2 border-t border-line bg-surface-muted px-5 py-3 rounded-b-lg',
        className,
      )}
    >
      {children}
    </div>
  );
}

export default Card;
